// Типы, перемещённые в shared для соблюдения FSD-архитектуры (реэкспорт)
export type {
  BlockComponentType,
  DisplayMode,
  BlockDisplayOptions,
  BlockCopyData,
  BlockVisible,
  ColorRule,
  HeadingStyles,
} from '../../shared/common/types/dashboard';

// Импортируем для использования внутри этого файла
import type {
  BlockComponentType,
  BlockDisplayOptions,
  BlockCopyData,
  BlockVisible,
  ColorRule,
  HeadingStyles,
} from '../../shared/common/types/dashboard';

// ==================== Базовые типы ====================

/**
 * Источник конфигурации блока дашборда.
 * - 'linked' — блок связан с сохранённым отчётом
 * - 'custom' — блок имеет собственную конфигурацию
 */
export type ConfigSource = 'linked' | 'custom';

/**
 * Тип отчёта для ReportChart.
 */
export type ChartReportType = 'registration' | 'order' | 'payment' | 'ad' | 'webinar' | 'visit';

/**
 * Тип отчёта для ReportTable.
 */
export type TableReportType = 'universal' | 'order' | 'payment' | 'ad' | 'webinar' | 'visit';

// ==================== Layout типы ====================

/**
 * Позиция и размер блока в grid-сетке.
 */
export interface GridItemLayout {
  /** Уникальный идентификатор блока в сетке */
  i: string;
  /** Позиция по горизонтали (в единицах сетки) */
  x: number;
  /** Позиция по вертикали (в единицах сетки) */
  y: number;
  /** Ширина (в единицах сетки) */
  w: number;
  /** Высота (в единицах сетки) */
  h: number;
  /** Минимальная ширина */
  minW?: number;
  /** Минимальная высота */
  minH?: number;
  /** Максимальная ширина */
  maxW?: number;
  /** Максимальная высота */
  maxH?: number;
  /** Флаг, что данные уже масштабированы */
  scaled?: boolean;
  /** Флаг, что блок был перемещён */
  moved?: boolean;
  /** Флаг, что блок статичный (нельзя перемещать) */
  static?: boolean;
}

// ==================== Данные блока ====================


/**
 * Данные блока дашборда.
 */
export interface DashboardBlockData {
  /** Уникальный идентификатор блока */
  blockId: string;
  /** Тип графика (для chart-блоков) */
  chartType?: string;
  /** Видимость оси Y (для chart-блоков с line/area) */
  isAxesVisible?: boolean;
  /** Настройки атрибуции */
  attribution?: {
    attribution?: string | string[];
    type_applications?: string | string[];
  };
  /** Детализация */
  details?: unknown;
  /** Тип даты/периода */
  date?: string;
  /** Заголовок блока */
  title?: string;
  /** Тип компонента */
  componentType?: BlockComponentType | string;
  /** Описание блока */
  description?: string;
  /** Текст заголовка (для heading) */
  heading?: string;

  /**
   * Источник конфигурации блока.
   * - 'linked' — блок связан с сохранённым отчётом
   * - 'custom' — блок имеет собственную конфигурацию (по умолчанию)
   */
  configSource?: ConfigSource;

  /**
   * ID связанного сохранённого отчёта (если configSource === 'linked').
   */
  linkedReportId?: number;

  /**
   * Hash связанного отчёта для быстрого доступа.
   */
  linkedReportHash?: string;

  /**
   * Название связанного отчёта (для отображения в UI).
   */
  linkedReportName?: string;

  /**
   * Опции отображения для блока.
   */
  displayOptions?: BlockDisplayOptions;

  /** Стили заголовка (для heading) */
  headingStyles?: HeadingStyles;
  /** Правила условной раскраски числа (для number-блоков) */
  colorRules?: ColorRule[];

  /** Метрики блока */
  metrics?: any[];
  /** Группировки блока */
  groupings?: any[];
  /** Выбранные метрики */
  selectedMetrics?: any[];
  /** Выбранные группировки */
  selectedGroupings?: any[];
  /** Отмеченные метрики (для отображения на графике) */
  metricsChecked?: any[];
  /** Отмеченные группировки */
  groupsChecked?: any[];
  /** Отмеченные итоги */
  totalChecked?: any[];
  /** Проценты */
  percentages?: any[];
  /** Колонки со спарклайном (dataKey) */
  sparklines?: string[];
  /** Заливка ячеек обычных метрик по условиям «от – до» (dataKey → настройки, CellFillSettings) */
  cellFills?: Record<string, any>;
  /** «Динамика»: проценты к прошлому периоду под числами */
  analysis?: boolean;
  /** Заливка всей таблицы (кнопка над таблицей) */
  cellFilling?: boolean;
  /** Фильтры таблицы */
  filtersTable?: any[];
  /** Сортировка таблицы */
  sortTable?: any;
  /** Режим выбора группировок: 'manual' (ручной), 'all' (все), 'firstN' (первые N строк) */
  groupsSelectionMode?: 'manual' | 'all' | 'firstN';
  /** Количество первых строк для режима 'firstN' */
  selectFirstNRows?: number | null;
  /** Фильтры блока */
  filters?: any[];
  /** Родительские фильтры */
  filterParent?: any[];

  /** Дополнительные поля */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Конфигурация связи блока с сохранённым отчётом.
 */
export interface LinkedReportConfig {
  /** ID сохранённого отчёта */
  linkedReportId: number;
  /** Hash отчёта для быстрого доступа */
  linkedReportHash?: string;
  /** Название связанного отчёта (для отображения) */
  linkedReportName?: string;
}

// ==================== Компонент дашборда ====================

/**
 * Компонент дашборда (блок).
 */
export interface DashboardComponent {
  /** Заголовок блока */
  title?: string;
  /** Тип компонента */
  componentType: BlockComponentType | string;
  /** Данные позиции и размера в сетке */
  layoutData: GridItemLayout;
  /** Данные блока */
  data: DashboardBlockData;
}

// ==================== Состояния модальных окон ====================

/**
 * Состояние модального окна редактирования блока.
 */
export interface EditBlockState {
  visible: boolean;
  id: string;
  data: DashboardComponent | Record<string, never>;
}

/**
 * Состояние модального окна полного отчёта.
 */
export interface FullReportModalState {
  visible: boolean;
  blockId?: string;
  title?: string;
  date?: string;
  componentType?: string;
}

// ==================== Вспомогательные типы для метрик/группировок ====================

/**
 * Элемент метрики.
 */
export interface MetricItem {
  dataKey?: string;
  key?: string;
  name?: string;
  label?: string;
  [key: string]: unknown;
}

/**
 * Ссылка на метрику (для metricsChecked).
 */
export interface MetricRef {
  dataKey: string;
  name?: string;
  label?: string;
}

/**
 * Элемент группировки.
 */
export interface GroupingItem {
  dataKey?: string;
  key?: string;
  name?: string;
  label?: string;
  pathStr?: string;
  [key: string]: unknown;
}

/**
 * Ссылка на группировку.
 */
export interface GroupingRef {
  dataKey?: string;
  pathStr?: string;
  label?: string;
  [key: string]: unknown;
}

/**
 * Элемент итогов.
 */
export interface TotalItem {
  label: string;
  pathStr: string;
}

/**
 * Элемент процентов.
 */
export interface PercentageItem {
  [key: string]: unknown;
}

/**
 * Элемент фильтра.
 */
export interface FilterItem {
  [key: string]: unknown;
}

/**
 * Элемент сортировки.
 */
export interface SortItem {
  [key: string]: unknown;
}

// ==================== Константы сетки ====================

/**
 * Константы для grid-сетки дашборда.
 */
export const GRID_CONSTANTS = {
  /** Коэффициент масштабирования по горизонтали */
  SCALE_X: 10,
  /** Ширина одного шага по горизонтали в px */
  CELL_WIDTH: 10,
  /** Высота одного шага по вертикали в px */
  CELL_HEIGHT: 10,
  /** Отступ между блоками в px */
  MARGIN: 15,
  /** Минимальное количество колонок */
  MIN_COLS: 500,
  /** Минимальное количество строк */
  MIN_ROWS: 500,
  /** Минимальная ширина блока (в единицах сетки) — fallback */
  MIN_BLOCK_WIDTH: 15,
  /** Минимальная высота блока (в единицах сетки) — fallback */
  MIN_BLOCK_HEIGHT: 8,
} as const;

/**
 * Минимальные размеры блоков по типу компонента (в единицах сетки).
 * 1 единица = CELL_WIDTH (10px), между ячейками MARGIN (15px).
 * Формула px: units * 10 + (units - 1) * 15 = 25 * units - 15
 */
export const BLOCK_MIN_SIZES: Record<string, { minW: number; minH: number }> = {
  chart:   { minW: 15, minH: 10 },  // ~360×235 px
  table:   { minW: 15, minH: 8 },   // ~360×185 px
  number:  { minW: 6,  minH: 3 },   // ~135×60 px
  heading: { minW: 5,  minH: 3 },   // ~110×60 px
};